/**
 * AddCalendarEventModal — shared create/edit modal for Studio Calendar events.
 *
 * `kind: 'pto' | 'holiday'` picks the field set. For 'pto', a non-admin is locked to logging
 * time off for themselves (currentMember); an admin gets a team-member picker so they can log
 * or correct PTO on anyone's behalf — see docs/STUDIO_CALENDAR.md "Decisions" for why there's no
 * per-row ownership lock beyond that. Styling mirrors TrackerWorkspace.jsx's EntryFormModal (M).
 */

import { useEffect, useState } from 'react'
import { getTeamMembers } from '../services/members.js'
import { createPtoWithTimeCard, updatePto, createHoliday, updateHoliday } from '../services/studioCalendar.js'
import { alertDialog } from './Dialog.jsx'

export default function AddCalendarEventModal({ kind, event, isAdmin, currentMember, onClose, onSaved }) {
  const isEdit = !!event
  const [memberId, setMemberId] = useState(event ? event.member_airtable_id : (currentMember ? currentMember.id : ''))
  const [teamMembers, setTeamMembers] = useState(null)
  const [holidayName, setHolidayName] = useState(event ? (event.holiday_name || '') : '')
  const [startDate, setStartDate] = useState(event ? event.start_date : '')
  const [endDate, setEndDate] = useState(event ? event.end_date : '')
  const [ptoKind, setPtoKind] = useState(event ? (event.pto_kind || 'vacation') : 'vacation')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (kind === 'pto' && isAdmin) getTeamMembers().then(setTeamMembers).catch(() => setTeamMembers([]))
  }, [kind, isAdmin])

  function setStart(v) {
    setStartDate(v)
    if (!endDate || endDate < v) setEndDate(v)
  }

  async function save() {
    if (!startDate || !endDate) { setError('Start and end date are required.'); return }
    if (endDate < startDate) { setError('End date must be on or after the start date.'); return }
    if (kind === 'holiday' && !holidayName.trim()) { setError('Holiday name is required.'); return }
    if (kind === 'pto' && !memberId) { setError('Select who this time off is for.'); return }

    setSaving(true); setError('')
    try {
      if (kind === 'pto') {
        const memberName = isAdmin ? ((teamMembers || []).find(m => m.id === memberId) || {}).name : (currentMember && currentMember.name)
        if (isEdit) await updatePto(event.id, { memberAirtableId: memberId, memberName, startDate, endDate, kind: ptoKind })
        else {
          // Your own days off make draft time entries (api/_ptoLink.js). Say so, and that submitting
          // them is still yours to do (Matt, 2026-10-19).
          const r = await createPtoWithTimeCard({ memberAirtableId: memberId, memberName, startDate, endDate, kind: ptoKind })
          const tc = (r && r.timeCard) || {}
          const fmt = d => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
          onSaved()
          if (tc.created && tc.created.length) {
            await alertDialog({
              title: 'Time card entries created',
              message: `${tc.created.length === 1 ? 'A time card entry was' : tc.created.length + ' time card entries were'} created for ${tc.created.map(fmt).join(', ')} (${ptoKind === 'sick' ? 'sick leave' : 'vacation'}). They are NOT submitted. Open your time card, check ${tc.created.length === 1 ? 'it' : 'them'}, and submit ${tc.created.length === 1 ? 'it' : 'them'} yourself.`,
              okLabel: 'Got it',
            })
          } else if (tc.forSomeoneElse) {
            await alertDialog({ title: 'Time off logged', message: `Logged for ${memberName || 'them'}. Their time card entries aren\u2019t made for them; they add and submit their own.`, okLabel: 'OK' })
          }
          return
        }
      } else {
        if (isEdit) await updateHoliday(event.id, { holidayName: holidayName.trim(), startDate, endDate })
        else await createHoliday({ holidayName: holidayName.trim(), startDate, endDate })
      }
      onSaved()
    } catch (e) {
      setError(e.message || 'Save failed.')
    } finally {
      setSaving(false)
    }
  }

  const title = kind === 'pto'
    ? (isEdit ? 'Edit time off' : 'Log time off')
    : (isEdit ? 'Edit studio holiday' : 'Add studio holiday')

  return (
    <div style={M.overlay} onClick={onClose}>
      <div style={M.card} onClick={e => e.stopPropagation()}>
        <div style={M.head}>
          <span style={M.title}>{title}</span>
          <button style={M.x} onClick={onClose}>&times;</button>
        </div>
        <div style={M.body}>
          {error && <div style={M.notice}>{error}</div>}

          {kind === 'pto' && (
            <div style={M.field}>
              <div style={M.fieldLabel}>For</div>
              {isAdmin ? (
                <select style={M.input} value={memberId} onChange={e => setMemberId(e.target.value)}>
                  <option value="">Select a team member…</option>
                  {(teamMembers || []).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              ) : (
                <div style={M.staticVal}>{currentMember ? currentMember.name : 'Me'}</div>
              )}
            </div>
          )}

          {kind === 'pto' && (
            <div style={M.field}>
              <div style={M.fieldLabel}>Kind</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {[['vacation', 'Vacation / PTO'], ['sick', 'Sick']].map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setPtoKind(k)} style={{
                    padding: '5px 12px', borderRadius: 20, cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5,
                    border: '0.5px solid ' + (ptoKind === k ? '#07272D' : 'var(--aj-border-mid)'),
                    background: ptoKind === k ? '#07272D' : 'transparent', color: ptoKind === k ? '#fff' : 'var(--aj-text)',
                  }}>{label}</button>
                ))}
              </div>
            </div>
          )}

          {kind === 'holiday' && (
            <div style={M.field}>
              <div style={M.fieldLabel}>Holiday name</div>
              <input style={M.input} value={holidayName} onChange={e => setHolidayName(e.target.value)} placeholder="e.g. Memorial Day" />
            </div>
          )}

          <div style={M.row}>
            <div style={{ ...M.field, flex: 1 }}>
              <div style={M.fieldLabel}>Start date</div>
              <input type="date" style={M.input} value={startDate} onChange={e => setStart(e.target.value)} />
            </div>
            <div style={{ ...M.field, flex: 1 }}>
              <div style={M.fieldLabel}>End date</div>
              <input type="date" style={M.input} value={endDate} min={startDate || undefined} onChange={e => setEndDate(e.target.value)} />
            </div>
          </div>
        </div>
        <div style={M.foot}>
          <button style={M.ghost} onClick={onClose}>Cancel</button>
          <button style={M.primary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  )
}

const M = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 700, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px', overflowY: 'auto' },
  card: { width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.3)' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid var(--aj-border-mid)' },
  title: { fontFamily: 'Montserrat,sans-serif', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 13, fontWeight: 700, color: 'var(--aj-text)' },
  x: { background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: 'var(--aj-text-muted)', cursor: 'pointer' },
  body: { padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 },
  foot: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '0.5px solid var(--aj-border-mid)' },
  field: { display: 'flex', flexDirection: 'column' },
  fieldLabel: { fontFamily: '"DM Mono",monospace', fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 4 },
  staticVal: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', padding: '7px 0' },
  input: { width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)' },
  row: { display: 'flex', gap: 10 },
  primary: { background: '#07272D', border: 'none', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: '#fff' },
  ghost: { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' },
  notice: { background: 'rgba(184,134,11,0.1)', border: '0.5px solid rgba(184,134,11,0.3)', borderRadius: 6, padding: '8px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#8a6508' },
}
